import type { PicklistItem } from '@efdigital/ll-addon-data';
import { DataTypeQuiz } from '@efdigital/ll-addon-data/data-types';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { Type } from '@warp-drive/core/types/symbols';

import { PicklistItemPicker } from '../../../../pickers/picklist-item-picker.gts';
import type { QuizQuestionResponseEditorComponentSignature } from '../../types/question-manager.ts';

export class ReportingEditorComponent extends Component<QuizQuestionResponseEditorComponentSignature> {
  ensureReportingObject = () => {
    if (!this.args.question.reporting) {
      this.args.question.reporting = {
        [Type]: DataTypeQuiz.QuizReportingQuestion,
        groupName: '',
        questionName: '',
      };
    }
  };

  onGroupSelect = (item: PicklistItem | null) => {
    this.ensureReportingObject();
    this.args.question.reporting!.groupName = item?.title || '';
  };

  onQuestionSelect = (item: PicklistItem | null) => {
    this.ensureReportingObject();
    this.args.question.reporting!.questionName = item?.title || '';
  };

  onPreviewChange = (event: Event) => {
    const checked = (event.target as HTMLInputElement).checked;
    this.args.question.preview = checked;
  };

  get groupName(): string {
    return this.args.question.reporting?.groupName || '';
  }

  get questionName(): string {
    return this.args.question.reporting?.questionName || '';
  }

  <template>
    <div class="card-body">
      <h4 class="gud-eyebrow">HR Dashboard (Luzmo)</h4>

      <div class="form-text mb-3">
        These settings ensure that the question name is consistent across all
        questions in all quizzes, regardless of wording variations or language
        differences. The questions can be grouped into common categories to make
        it easier to find and understand the results.
      </div>

      {{#unless @quiz.reportAnswers}}
        {{#unless @quiz.reportAnswersInternal}}
          <div class="alert alert-danger mb-3">
            <strong>Reporting is disabled for this quiz.</strong>
            These settings will have no effect until reporting is enabled.
          </div>
        {{/unless}}
      {{/unless}}

      <PicklistItemPicker
        @label="Reporting group name"
        @group="quiz-reporting-group-names"
        @name="reporting-group-name"
        @selected={{this.groupName}}
        @selectedIsTitle={{true}}
        @readonly={{false}}
        @allowBlank={{true}}
        @blankLabel="(This question does not belong to a group)"
        @onSelect={{this.onGroupSelect}}
        class="mb-3"
      />

      <PicklistItemPicker
        @label="Standard question name"
        @group="quiz-reporting-question-names"
        @name="reporting-question-name"
        @selected={{this.questionName}}
        @selectedIsTitle={{true}}
        @readonly={{false}}
        @allowBlank={{true}}
        @blankLabel="(Use the question name as is)"
        @onSelect={{this.onQuestionSelect}}
        class="mb-3"
      />

      <hr />

      <h4 class="gud-eyebrow">Admin</h4>
      <div class="form-text mb-3">
        You can view all the answers to this quiz in the admin site. This allows
        you to see the raw data and export the results to a CSV file.
      </div>

      {{#let (uniqueId) as |uniqueId|}}
        <div class="form-check">
          <input
            id={{uniqueId}}
            type="checkbox"
            class="form-check-input"
            checked={{@question.preview}}
            {{on "change" this.onPreviewChange}}
          />
          <label for={{uniqueId}} class="form-check-label">
            Show this question in the table of responses
          </label>
        </div>
      {{/let}}
    </div>
  </template>
}
